import React, { useState, useContext } from "react";
import Ctx from "../../contexts/AppContext";
import { btnP, sp, rad, icBox } from "../../constants/styles";
import UpgradeModal from "./UpgradeModal";
import { getPlanUsage } from "../../utils/plans";

/**
 * Feature Gate — blocks access to features not in the current plan.
 * Shows a friendly upgrade prompt instead of the locked page.
 *
 * Usage: <FeatureGate feature="maintenance"><MaintPage /></FeatureGate>
 */

const FEATURE_NAMES = {
  basic_dashboard: "Dashboard",
  payments: "Payments",
  maintenance: "Maintenance",
  whatsapp: "WhatsApp Notifications",
  leases: "Lease & Agreements",
  service_persons: "Service Persons",
  agreements: "Rental Agreements",
  commercial: "Commercial Properties",
  reports: "Reports & Analytics",
  csv_export: "CSV Export",
  analytics: "AI Assistant & Analytics",
  automation: "Automation",
  multi_user: "Multi-User / Team",
  priority_support: "Priority Support"
};

const FEATURE_ICONS = {
  maintenance: "\uD83D\uDD27",
  leases: "\uD83D\uDCC4",
  service_persons: "\uD83D\uDC77",
  agreements: "\uD83D\uDCDD",
  reports: "\uD83D\uDCCA",
  analytics: "\uD83E\uDD16",
  multi_user: "\uD83D\uDC65",
  csv_export: "\uD83D\uDCE5",
  automation: "\u26A1",
  commercial: "\uD83C\uDFE2"
};

// Which plan unlocks each feature (for display)
const FEATURE_MIN_PLAN = {
  maintenance: "Starter",
  leases: "Starter",
  service_persons: "Starter",
  whatsapp: "Starter",
  agreements: "Growth",
  commercial: "Growth",
  reports: "Growth",
  csv_export: "Growth",
  analytics: "Pro",
  automation: "Pro",
  multi_user: "Pro",
  priority_support: "Pro"
};

export default function FeatureGate({ feature, children }) {
  const { h, t, plan, role, refreshData, flash } = useContext(Ctx);
  const [showUpgrade, setShowUpgrade] = useState(false);

  // Only gate landlord features
  if (role !== "landlord") return children;

  const features = plan.features || [];

  // If feature is in current plan, render children
  if (features.includes(feature)) return children;

  const usage = getPlanUsage(plan.type, plan.currentCount || 0, plan.maxProperties);
  const featureName = FEATURE_NAMES[feature] || feature;
  const featureIcon = FEATURE_ICONS[feature] || "\uD83D\uDD12";
  const minPlan = FEATURE_MIN_PLAN[feature] || "Starter";

  return (
    <div style={{ padding: sp.xl, paddingTop: 40, textAlign: "center" }} className="animate-fadeIn">
      {/* Lock icon */}
      <div style={{
        width: 72, height: 72, borderRadius: 20,
        background: `${h.ac}10`, display: "flex", alignItems: "center",
        justifyContent: "center", margin: "0 auto", marginBottom: sp.lg
      }}>
        <span style={{ fontSize: 36 }}>{featureIcon}</span>
      </div>

      {/* Title */}
      <h2 style={{
        color: h.tx, fontSize: 20, fontWeight: 700,
        margin: 0, marginBottom: sp.sm, letterSpacing: "-0.3px"
      }}>
        {featureName}
      </h2>

      {/* Subtitle */}
      <p style={{
        color: h.ts, fontSize: 13, margin: 0,
        marginBottom: sp.xl, lineHeight: 1.5, maxWidth: 280,
        marginLeft: "auto", marginRight: "auto"
      }}>
        This feature is available on the <strong style={{ color: h.ac }}>{minPlan}</strong> plan and above.
        Upgrade to unlock {featureName.toLowerCase()}.
      </p>

      {/* Current plan badge */}
      <div style={{
        display: "inline-flex", alignItems: "center", gap: sp.sm,
        background: h.sf, borderRadius: rad.md, padding: `${sp.sm}px ${sp.lg}px`,
        marginBottom: sp.xl, border: `1px solid ${h.bd}`
      }}>
        <span style={{ fontSize: 11, color: h.ts }}>Current plan:</span>
        <span style={{
          fontSize: 12, fontWeight: 700, color: h.tx,
          textTransform: "capitalize"
        }}>{plan.name || plan.type || "Free"}</span>
      </div>

      {/* Upgrade button */}
      <div>
        <button
          onClick={() => setShowUpgrade(true)}
          style={{
            ...btnP(h), width: "100%", maxWidth: 300,
            padding: `${sp.md + 2}px ${sp.xl}px`, fontSize: 14,
            fontWeight: 700, boxShadow: `0 4px 16px ${h.ac}30`,
            display: "flex", alignItems: "center", justifyContent: "center",
            gap: sp.sm, margin: "0 auto"
          }}
        >
          <span>{"\uD83D\uDE80"}</span>
          Upgrade to {minPlan}
        </button>
      </div>

      {/* Feature highlights */}
      <div style={{
        marginTop: sp.xxl, background: h.cd, borderRadius: rad.lg,
        padding: sp.lg, border: `1px solid ${h.bd}`, textAlign: "left",
        maxWidth: 300, marginLeft: "auto", marginRight: "auto"
      }}>
        <div style={{ color: h.tx, fontSize: 12, fontWeight: 600, marginBottom: sp.md }}>
          {minPlan} plan includes:
        </div>
        {Object.entries(FEATURE_NAMES).filter(([key]) => {
          // Show features up to the minPlan level
          const planOrder = { Starter: 1, Growth: 2, Pro: 3 };
          const fPlan = FEATURE_MIN_PLAN[key];
          return !fPlan || (planOrder[fPlan] || 0) <= (planOrder[minPlan] || 1);
        }).slice(0, 8).map(([key, name]) => (
          <div key={key} style={{
            display: "flex", alignItems: "center", gap: sp.sm,
            marginBottom: sp.xs + 2
          }}>
            <span style={{ color: h.gn, fontSize: 11, fontWeight: 700 }}>{"\u2713"}</span>
            <span style={{
              color: key === feature ? h.ac : h.tx,
              fontSize: 11, fontWeight: key === feature ? 600 : 400
            }}>{name}</span>
          </div>
        ))}
      </div>

      {showUpgrade && (
        <UpgradeModal
          h={h} t={t}
          used={usage.used} limit={usage.limit}
          currentPlan={plan.type}
          onClose={() => setShowUpgrade(false)}
          onUpgradeSuccess={async () => {
            if (refreshData) await refreshData();
            flash(t.billingSuccess || "Plan Upgraded!");
            setShowUpgrade(false);
          }}
        />
      )}
    </div>
  );
}
